import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Shirt, HardHat, Crown, Package, Box } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const fmtIso = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}.`;
};

function iconForType(type) {
  if (!type) return Package;
  if (type.startsWith("polo")) return Shirt;
  if (type === "manager_cap") return Crown;
  if (type === "worker_cap") return HardHat;
  return Package;
}

export default function PortalWorkwear() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/portal/workwear");
        setData(data);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || t("common.error"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grouped = useMemo(() => {
    const items = data?.items || [];
    return {
      active: items.filter((i) => !i.returned),
      returned: items.filter((i) => i.returned),
    };
  }, [data]);

  if (loading) {
    return (
      <div className="space-y-3 px-4 pb-8 pt-6 md:px-6 md:pt-8">
        {["s1", "s2", "s3"].map((k) => (
          <div key={k} className="h-20 animate-pulse rounded-2xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900" />
        ))}
      </div>
    );
  }

  const activeCount = grouped.active.reduce((acc, it) => acc + (it.quantity || 1), 0);
  const returnedCount = grouped.returned.reduce((acc, it) => acc + (it.quantity || 1), 0);

  return (
    <div className="space-y-6 px-4 pb-8 pt-6 md:px-6 md:pt-8">
      <header>
        <p className="text-[14px] text-zinc-500">{t("workwear.title")}</p>
        <h1 className="mt-1 text-[28px] font-semibold tracking-tight md:text-[32px]">
          {t("workwear.item")}
        </h1>
      </header>

      {/* Top stats */}
      <section className="grid grid-cols-2 gap-3">
        <StatCard
          label={t("workwear.current_total")}
          count={activeCount}
          types={grouped.active.length}
          tone="active"
        />
        <StatCard
          label={t("workwear.returned")}
          count={returnedCount}
          types={grouped.returned.length}
          tone="muted"
        />
      </section>

      {/* Active group */}
      <section>
        <h2 className="mb-3 px-1 text-[18px] font-semibold tracking-tight">
          {t("workwear.active_items")}
        </h2>
        {grouped.active.length === 0 ? (
          <EmptyState message={t("workwear.no_current")} />
        ) : (
          <ItemList items={grouped.active} />
        )}
      </section>

      {/* Returned group */}
      {grouped.returned.length > 0 && (
        <section>
          <h2 className="mb-3 px-1 text-[18px] font-semibold tracking-tight">
            {t("workwear.returned")}
          </h2>
          <ItemList items={grouped.returned} />
        </section>
      )}
    </div>
  );
}

function StatCard({ label, count, types, tone }) {
  const { t } = useTranslation();
  const active = tone === "active";
  return (
    <div className="rounded-2xl bg-white p-5 shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_-1px_rgba(0,0,0,0.02)] dark:bg-zinc-900">
      <div className="flex items-center justify-between">
        <p className="text-[12.5px] text-zinc-500">{label}</p>
        {active && count > 0 && (
          <span className="grid h-6 w-6 place-items-center rounded-full bg-emerald-50 dark:bg-emerald-950/30">
            <Box className="h-3 w-3 text-emerald-600 dark:text-emerald-400" strokeWidth={2.2} />
          </span>
        )}
      </div>
      <p
        className={
          "mt-2 font-mono text-[32px] font-semibold leading-none tabular-nums tracking-[-0.02em] " +
          (active ? "" : "text-zinc-500")
        }
      >
        {count}<span className="ml-1.5 text-[14px] font-medium text-zinc-400">{t("workwear.pcs")}</span>
      </p>
      <p className="mt-2 text-[11.5px] text-zinc-400">
        {t("workwear.types_count", { count: types })}
      </p>
    </div>
  );
}

function ItemList({ items }) {
  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_-1px_rgba(0,0,0,0.02)] dark:bg-zinc-900">
      {items.map((it, i) => (
        <ItemRow key={it.id} item={it} last={i === items.length - 1} />
      ))}
    </div>
  );
}

function ItemRow({ item, last }) {
  const { t } = useTranslation();
  const Icon = iconForType(item.item_type);
  const returned = item.returned;
  return (
    <>
      <div
        data-testid={`workwear-item-${item.id}`}
        className="flex items-center gap-4 px-5 py-4"
      >
        <div
          className={
            "grid h-11 w-11 shrink-0 place-items-center rounded-2xl " +
            (returned
              ? "bg-zinc-100 text-zinc-400 dark:bg-zinc-800/60"
              : "bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400")
          }
        >
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <p
            className={
              "text-[14.5px] font-semibold tracking-tight " +
              (returned ? "text-zinc-500" : "")
            }
          >
            {item.item_label || "—"}
          </p>
          <p className="mt-0.5 text-[12px] text-zinc-500">
            {t("workwear.received_at", { date: fmtIso(item.issued_at) })}
            {item.returned_at && ` · ${t("workwear.returned_at", { date: fmtIso(item.returned_at) })}`}
          </p>
          {item.issuer && (
            <p className="mt-0.5 text-[11px] text-zinc-400">
              {item.issuer}
            </p>
          )}
        </div>
        <div className="text-right">
          <p className="font-mono text-[15px] font-semibold tabular-nums">{item.quantity}×</p>
          <span
            className={
              "mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-medium " +
              (returned
                ? "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400"
                : "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300")
            }
          >
            {returned ? t("workwear.returned") : t("workwear.active")}
          </span>
        </div>
      </div>
      {!last && <div className="mx-5 h-px bg-zinc-100 dark:bg-zinc-800/60" />}
    </>
  );
}

function EmptyState({ message }) {
  return (
    <div className="rounded-2xl bg-white p-10 text-center shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900">
      <div className="mx-auto grid h-10 w-10 place-items-center rounded-full bg-zinc-100 text-zinc-400 dark:bg-zinc-800">
        <Package className="h-4 w-4" strokeWidth={1.8} />
      </div>
      <p className="mt-3 text-[13.5px] text-zinc-500">{message}</p>
    </div>
  );
}
